import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { formatDate } from '../lib/constants'
import Header from '../components/Header'
import AdminBulkEmail from '../components/AdminBulkEmail'
import ListingPlaceholder from '../components/ListingPlaceholder'
import StripeReminderModal from '../components/StripeReminderModal'
import AdminConversations from '../components/AdminConversations'
import { CATEGORIES } from '../lib/constants'
import { CONTACT_KIND_LABELS, detectContactDetails, blockingKinds } from '../lib/contactDetection'

// Real booking requests: not a plain message ('enquiry'), linked to a
// listing, not someone booking their own item, and not marked as test
const realRequests = (rows) => (rows || []).filter(b => b.status !== 'enquiry' && b.listing_id && b.hirer_id !== b.owner_id && !b.is_test)
// Paid = payment taken or held, and not cancelled or refunded since
const isPaidBooking = (b) => !!b.paid_at && b.status !== 'cancelled' && !['cancelled', 'refunded'].includes(b.payment_status)

const TIME_FILTERS = [
{ label: 'Today', value: 'today', days: 1 },
{ label: 'This week', value: 'week', days: 7 },
{ label: 'This month', value: 'month', days: 30 },
{ label: 'All time', value: 'all', days: null },
]

export default function Admin() {
const { user, profile, loading: authLoading } = useAuth()
const navigate = useNavigate()
const [stats, setStats] = useState({
users: 0, listings: 0, bookings: 0, revenue: 0, platformFees: 0,
activeListings: 0, completedBookings: 0, cancelledBookings: 0,
pendingBookings: 0, paidBookings: 0,
deletedUsers: 0, removedListings: 0,
})
const [users, setUsers] = useState([])
const [listings, setListings] = useState([])
const [bookings, setBookings] = useState([])
const [reports, setReports] = useState([])
const [deletedAccounts, setDeletedAccounts] = useState([])
const [removedListings, setRemovedListings] = useState([])
const [searchMisses, setSearchMisses] = useState([])
const [funnelData, setFunnelData] = useState({ listers: new Set(), requested: new Set(), completed: new Set() })
const [funnelStage, setFunnelStage] = useState(null)
const [showStripeReminder, setShowStripeReminder] = useState(false)
const [wantedList, setWantedList] = useState([])
const [wantedReports, setWantedReports] = useState([])
const [contactFlags, setContactFlags] = useState([])
const [flagNames, setFlagNames] = useState({})
const [flagUser, setFlagUser] = useState(null)
const [showRemovedWanted, setShowRemovedWanted] = useState(false)
const [viewWanted, setViewWanted] = useState(null)
const [loading, setLoading] = useState(true)
const [tab, setTab] = useState('overview')
const [timeFilter, setTimeFilter] = useState('all')
const [error, setError] = useState('')
const [searchTerm, setSearchTerm] = useState('')

useEffect(() => {
if (authLoading) return
if (!user) return // ProtectedRoute sends logged-out visitors to sign-in
if (!profile) return
if (!profile.is_admin) {
navigate('/')
return
}
fetchAll()
}, [user, authLoading, profile, navigate])

useEffect(() => {
if (profile?.is_admin) fetchAll()
}, [timeFilter])

const getDateCutoff = (filter) => {
const f = TIME_FILTERS.find(t => t.value === filter)
if (!f || f.days === null) return null
const cutoff = new Date()
cutoff.setDate(cutoff.getDate() - f.days)
return cutoff.toISOString()
}

const fetchAll = async () => {
setLoading(true)
setError('')
try {
const cutoff = getDateCutoff(timeFilter)

let profilesQuery = supabase.from('profiles').select('*').order('created_at', { ascending: false })
let listingsQuery = supabase.from('listings').select('*, profiles(full_name)').order('created_at', { ascending: false })
let bookingsQuery = supabase.from('bookings').select(`
*,
listings(title),
hirer:profiles!bookings_hirer_id_fkey(full_name),
owner:profiles!bookings_owner_id_fkey(full_name)
`).not('listing_id', 'is', null).order('created_at', { ascending: false })

// Removed tab + counters go by when it was removed, not when it was created
let deletedQuery = supabase.from('deleted_accounts_log').select('*').order('deleted_at', { ascending: false })
let removedQuery = supabase.from('listings').select('id, title, suburb, state, category_id, images, archived_at, removed_by, removal_reason, profiles(full_name)').not('archived_at', 'is', null).order('archived_at', { ascending: false })

let missesQuery = supabase.from('search_misses').select('*').order('searched_at', { ascending: false }).limit(2000)

if (cutoff) {
missesQuery = missesQuery.gte('searched_at', cutoff)
deletedQuery = deletedQuery.gte('deleted_at', cutoff)
removedQuery = removedQuery.gte('archived_at', cutoff)
profilesQuery = profilesQuery.gte('created_at', cutoff)
listingsQuery = listingsQuery.gte('created_at', cutoff)
bookingsQuery = bookingsQuery.gte('created_at', cutoff)
}

const [profilesRes, listingsRes, bookingsRes, reportsRes, deletedRes, removedRes, missesRes, ownerListingsRes, ownerBookingsRes] = await Promise.all([
profilesQuery,
listingsQuery,
bookingsQuery,
supabase.from('reports').select(`
*,
reporter:profiles!reports_reporter_id_fkey(full_name),
reported:profiles!reports_reported_id_fkey(full_name)
`).order('created_at', { ascending: false }).limit(50),
deletedQuery,
removedQuery,
missesQuery,
// Funnel: everything each owner has ever done, whatever the date filter
supabase.from('listings').select('owner_id, status'),
supabase.from('bookings').select('owner_id, hirer_id, listing_id, status, is_test'),
])

const b = bookingsRes.data || []
// Dashboard numbers: real activity only (no messages-only enquiries, no test bookings)
const real = realRequests(b)
const paid = real.filter(isPaidBooking)
const totalRevenue = paid.reduce((sum, booking) => sum + (booking.total_amount || 0), 0)
const totalFees = paid.reduce((sum, booking) => sum + (booking.platform_fee || 0), 0)

setStats({
users: (profilesRes.data || []).filter(p => !p.is_test_account).length,
listings: listingsRes.data?.length || 0,
activeListings: listingsRes.data?.filter(l => l.available).length || 0,
bookings: real.length,
completedBookings: real.filter(x => x.status === 'completed').length,
cancelledBookings: real.filter(x => x.status === 'cancelled').length,
pendingBookings: real.filter(x => x.status === 'pending').length,
paidBookings: paid.length,
revenue: totalRevenue,
platformFees: totalFees,
deletedUsers: deletedRes.data?.length || 0,
removedListings: removedRes.data?.length || 0,
})
setDeletedAccounts(deletedRes.data || [])
loadWantedAndFlags(cutoff)
setSearchMisses(missesRes.data || [])
setFunnelData({
// Published = a listing that went live (removed/disabled ones still count;
// never-verified 'pending_verification' ones don't)
listers: new Set((ownerListingsRes.data || []).filter(l => l.status === 'active').map(l => l.owner_id)),
// Real booking requests only: not a plain message ('enquiry'), still linked
// to a listing, and not the owner booking their own item
requested: new Set(realRequests(ownerBookingsRes.data).map(b => b.owner_id)),
completed: new Set(realRequests(ownerBookingsRes.data).filter(b => b.status === 'completed').map(b => b.owner_id)),
})
setRemovedListings(removedRes.data || [])
setUsers(profilesRes.data || [])
setListings(listingsRes.data || [])
setBookings(b)
setReports(reportsRes.data || [])
} catch (err) {
setError('Failed to load data: ' + err.message)
}
setLoading(false)
}

const toggleListing = async (listing) => {
const { error: toggleErr } = await supabase
.from('listings')
.update({ admin_disabled: !listing.admin_disabled })
.eq('id', listing.id)
if (toggleErr) {
setError('Failed to update listing: ' + toggleErr.message)
return
}
fetchAll()
}

const deleteListing = async (id) => {
const { data: bookings } = await supabase.from('bookings').select('id, status').eq('listing_id', id)
const allowedStatuses = ['enquiry', 'pending', 'cancelled', 'completed']
const hasBlockingBookings = bookings?.some(b => !allowedStatuses.includes(b.status))
if (hasBlockingBookings) {
alert("Can't remove this listing — it has active hires. Force-cancel them first.")
return
}
if (!window.confirm('Remove this listing? It will be hidden from the site and moved to the Removed tab.')) return
// Soft delete: kept for history (bookings, messages) and shown under Removed
const { error: removeErr } = await supabase.from('listings')
.update({ archived_at: new Date().toISOString(), removed_by: 'admin', removal_reason: 'Removed by admin' })
.eq('id', id)
if (removeErr) alert('Could not remove listing: ' + removeErr.message)
fetchAll()
}

const loadWantedAndFlags = async (cutoff) => {
let wq = supabase.from('wanted_requests').select('*').order('created_at', { ascending: false }).limit(500)
let fq = supabase.from('contact_flags').select('*').order('created_at', { ascending: false }).limit(1000)
if (cutoff) { wq = wq.gte('created_at', cutoff); fq = fq.gte('created_at', cutoff) }
const [wRes, rRes, fRes] = await Promise.all([
wq,
supabase.from('reports').select('id, reason, created_at, wanted_request_id').not('wanted_request_id', 'is', null).order('created_at', { ascending: false }),
fq,
])
const ids = [...new Set([...(wRes.data || []).map(w => w.user_id), ...(fRes.data || []).map(f => f.user_id)])]
let nameMap = {}
if (ids.length) {
const { data: people } = await supabase.from('profiles').select('id, full_name').in('id', ids)
nameMap = Object.fromEntries((people || []).map(x => [x.id, x.full_name || '(no name)']))
}
setWantedList(wRes.data || [])
setWantedReports(rRes.data || [])
setContactFlags(fRes.data || [])
setFlagNames(nameMap)
}

const removeWanted = async (w) => {
if (!window.confirm(`Remove "${w.title}" from the Wanted board? (It isn't deleted — just hidden.)`)) return
const { error: wErr } = await supabase.from('wanted_requests').update({ status: 'removed' }).eq('id', w.id)
if (wErr) { alert('Could not remove: ' + wErr.message); return }
fetchAll()
}

const toggleTestBooking = async (booking) => {
const { error: testErr } = await supabase.rpc('set_booking_test', { p_booking_id: booking.id, p_is_test: !booking.is_test })
if (testErr) { alert('Could not update: ' + testErr.message); return }
fetchAll()
}

const forceCancelBooking = async (booking) => {
if (!window.confirm(`Force-cancel booking "${booking.listings?.title}"? This bypasses the normal cancellation flow.`)) return
await supabase.from('bookings').update({
status: 'cancelled',
cancelled_at: new Date().toISOString(),
cancelled_by: 'admin',
cancellation_reason: 'Cancelled by HireIt admin',
}).eq('id', booking.id)
fetchAll()
}

// Loading state
if (authLoading || (user && !profile)) return (
<div>
<Header />
<div className="spinner-wrap">
<div className="spinner" />
</div>
<p style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 8 }}>Loading...</p>
</div>
)

// Not signed in
if (!user) return (
<div>
<Header />
<div className="spinner-wrap">
<div className="spinner" />
</div>
</div>
)

// Not admin — block access
if (!profile || !profile.is_admin) return (
<div>
<Header />
<div className="container section" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
<div style={{ fontSize: 64, marginBottom: 16 }}>🚫</div>
<h2 style={{ marginBottom: 8 }}>Access denied</h2>
<p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>This page is for HireIt administrators only.</p>
<button className="btn btn-gradient" onClick={() => navigate('/')}>Back to home</button>
</div>
</div>
)

// Still loading data
if (loading) return (
<div>
<Header />
<div className="spinner-wrap">
<div className="spinner" />
</div>
<p style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 8 }}>Loading admin dashboard...</p>
</div>
)

if (error) return (
<div>
<Header />
<div className="container section">
<div className="alert alert-error">{error}</div>
<button className="btn btn-primary" onClick={fetchAll}>Try again</button>
</div>
</div>
)

// Filter bookings needing attention
const refundsOwed = bookings.filter(b => b.refund_owed > 0 && b.status === 'cancelled' && b.payment_status === 'captured')
const disputedReturns = bookings.filter(b => b.owner_return_agreed === false)
const stuckPendingBookings = bookings.filter(b => {
if (b.status !== 'pending') return false
const age = (Date.now() - new Date(b.created_at).getTime()) / (1000 * 60 * 60)
return age > 48
})
const ownersWithoutStripe = users.filter(u => {
const hasListings = listings.some(l => l.owner_id === u.id)
return hasListings && !u.stripe_charges_enabled
})

const totalAlerts = refundsOwed.length + disputedReturns.length + stuckPendingBookings.length + reports.length

const filteredUsers = searchTerm
? users.filter(u =>
(u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(u.suburb || '').toLowerCase().includes(searchTerm.toLowerCase())
)
: users

const filteredListings = searchTerm
? listings.filter(l =>
(l.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(l.suburb || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(l.profiles?.full_name || '').toLowerCase().includes(searchTerm.toLowerCase())
)
: listings

const filteredBookings = searchTerm
? bookings.filter(b =>
(b.listings?.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(b.status || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(b.hirer?.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(b.owner?.full_name || '').toLowerCase().includes(searchTerm.toLowerCase())
)
: bookings

// ── Growth: owner funnel and signups by source (people who signed up in the selected period)
const FUNNEL_STAGES = [
{ id: 'signed_up', label: 'Signed up', test: () => true },
{ id: 'started', label: 'Started a listing', test: u => !!u.listing_started_at || funnelData.listers.has(u.id) },
{ id: 'published', label: 'Published a listing', test: u => funnelData.listers.has(u.id) },
{ id: 'stripe', label: 'Connected Stripe', test: u => !!u.stripe_charges_enabled },
{ id: 'requested', label: 'Received a booking request', test: u => funnelData.requested.has(u.id) },
{ id: 'completed', label: 'Completed a booking', test: u => funnelData.completed.has(u.id) },
]
const funnelUsers = users.filter(u => !u.is_admin && !u.is_test_account)
// Furthest stage reached in order (a later step only counts once the earlier ones are done)
const stageOf = (u) => {
let i = 0
while (i + 1 < FUNNEL_STAGES.length && FUNNEL_STAGES[i + 1].test(u)) i++
return i
}
const funnelIndex = new Map(funnelUsers.map(u => [u.id, stageOf(u)]))
const funnelCounts = FUNNEL_STAGES.map((_, i) => funnelUsers.filter(u => funnelIndex.get(u.id) >= i).length)
const stuckUsers = funnelStage === null ? [] : funnelUsers.filter(u => funnelIndex.get(u.id) === funnelStage)

const sourceRows = Object.values(funnelUsers.reduce((acc, u) => {
const key = u.signup_source || 'Direct / unknown'
acc[key] = acc[key] || { source: key, signups: 0, published: 0, stripe: 0 }
acc[key].signups++
if (funnelData.listers.has(u.id)) acc[key].published++
if (u.stripe_charges_enabled) acc[key].stripe++
return acc
}, {})).sort((a, b) => b.signups - a.signups)

// ── Searches with no results, grouped by term + place + category, newest first
const missGroups = Object.values(searchMisses.reduce((acc, m) => {
const key = [m.term, m.location, m.category].map(v => (v || '').toLowerCase()).join('|')
acc[key] = acc[key] || { key, term: m.term, location: m.location, category: m.category, count: 0, last: m.searched_at }
acc[key].count++
if (m.searched_at > acc[key].last) acc[key].last = m.searched_at
return acc
}, {})).sort((a, b) => (a.last < b.last ? 1 : -1))

const cell = { padding: '8px 12px', borderBottom: '1px solid var(--border)' }
const headCell = { ...cell, padding: '10px 12px', textAlign: 'left', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }
const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '—')

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 1100, padding: '1.5rem 1rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
<div>
<h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)' }}>📊 Admin Dashboard</h1>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Signed in as {user?.email}</div>
</div>
<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
<button className="btn btn-ghost btn-sm" onClick={fetchAll}>🔄 Refresh</button>
</div>
</div>

<div style={{ display: 'flex', gap: 6, marginBottom: '1rem', flexWrap: 'wrap' }}>
{TIME_FILTERS.map(f => (
<button
key={f.value}
onClick={() => setTimeFilter(f.value)}
className={`btn btn-sm ${timeFilter === f.value ? 'btn-primary' : 'btn-ghost'}`}
style={{ fontSize: 12 }}
>
{f.label}
</button>
))}
</div>

{totalAlerts > 0 && (
<div style={{ background: '#FFF8E7', border: '2px solid #F5C842', borderRadius: 12, padding: '14px 16px', marginBottom: '1.5rem' }}>
<div style={{ fontWeight: 700, marginBottom: 10, color: '#8a6d00', display: 'flex', alignItems: 'center', gap: 6 }}>
🚨 Items requiring your attention ({totalAlerts})
</div>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, fontSize: 13 }}>
{refundsOwed.length > 0 && (
<div onClick={() => setTab('refunds')} style={{ background: 'white', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>
<div style={{ color: '#DC2626', fontWeight: 700 }}>💰 {refundsOwed.length} refunds owed</div>
<div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Process in Stripe</div>
</div>
)}
{disputedReturns.length > 0 && (
<div onClick={() => setTab('disputes')} style={{ background: 'white', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>
<div style={{ color: '#DC2626', fontWeight: 700 }}>⚠️ {disputedReturns.length} disputed returns</div>
<div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Owner disagrees with condition</div>
</div>
)}
{stuckPendingBookings.length > 0 && (
<div onClick={() => setTab('bookings')} style={{ background: 'white', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>
<div style={{ color: '#8a6d00', fontWeight: 700 }}>⏰ {stuckPendingBookings.length} stuck pending</div>
<div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Pending 48+ hours</div>
</div>
)}
{reports.length > 0 && (
<div onClick={() => setTab('reports')} style={{ background: 'white', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>
<div style={{ color: '#DC2626', fontWeight: 700 }}>🚩 {reports.length} user reports</div>
<div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Review filed reports</div>
</div>
)}
</div>
</div>
)}

<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: '1.5rem' }}>
{[
{ label: 'New users', val: stats.users, color: 'var(--primary)' },
{ label: 'New listings', val: stats.listings, sub: `${stats.activeListings} active`, color: 'var(--accent)' },
{ label: 'New bookings', val: stats.bookings, sub: `${stats.paidBookings} paid · excl. tests & messages`, color: 'var(--navy)' },
{ label: 'Revenue', val: `$${stats.revenue.toFixed(2)}`, sub: 'Paid bookings only', color: '#16A34A' },
{ label: 'Platform fees', val: `$${stats.platformFees.toFixed(2)}`, sub: 'Your earnings', color: '#16A34A' },
{ label: 'Completed', val: stats.completedBookings, color: '#16A34A' },
{ label: 'Cancelled', val: stats.cancelledBookings, color: '#DC2626' },
{ label: 'Pending', val: stats.pendingBookings, color: '#F59E0B' },
{ label: 'Deleted users', val: stats.deletedUsers, color: 'var(--text-muted)' },
{ label: 'Removed listings', val: stats.removedListings, color: 'var(--text-muted)' },
].map(s => (
<div key={s.label} style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--border)' }}>
<div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
<div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.val}</div>
{s.sub && <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>{s.sub}</div>}
</div>
))}
</div>

<div style={{ background: 'var(--primary-pale)', borderRadius: 12, padding: '12px 14px', marginBottom: '1.5rem' }}>
<div style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', marginBottom: 8 }}>🔗 Quick external links</div>
<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
<a href="https://dashboard.stripe.com/payments" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>💳 Stripe Payments</a>
<a href="https://dashboard.stripe.com/connect/accounts/overview" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>🏦 Stripe Connect</a>
<a href="https://supabase.com/dashboard/projects" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>🗄️ Supabase</a>
<a href="https://resend.com/emails" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>📧 Resend</a>
<a href="https://vercel.com/dashboard" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>▲ Vercel</a>
</div>
</div>

<div style={{ display: 'flex', gap: 6, marginBottom: '1rem', flexWrap: 'wrap', overflowX: 'auto' }}>
{[
{ id: 'overview', label: '📋 Overview' },
{ id: 'users', label: '👥 Users' },
{ id: 'listings', label: '📦 Listings' },
{ id: 'bookings', label: '📅 Bookings' },
{ id: 'refunds', label: `💰 Refunds${refundsOwed.length > 0 ? ` (${refundsOwed.length})` : ''}` },
{ id: 'disputes', label: `⚠️ Disputes${disputedReturns.length > 0 ? ` (${disputedReturns.length})` : ''}` },
{ id: 'reports', label: `🚩 Reports${reports.length > 0 ? ` (${reports.length})` : ''}` },
{ id: 'removed', label: '🗑️ Removed' },
{ id: 'growth', label: '📈 Growth' },
{ id: 'wanted', label: `🔎 Wanted${wantedReports.length ? ` (${wantedReports.length} reports)` : ''}` },
{ id: 'conversations', label: '💬 Conversations' },
{ id: 'flags', label: `🚩 Flags${contactFlags.length ? ` (${contactFlags.length})` : ''}` },
{ id: 'searches', label: '🔍 Searches' },
{ id: 'email', label: '📧 Email' },
].map(t => (
<button
key={t.id}
className={`btn btn-sm ${tab === t.id ? 'btn-primary' : 'btn-ghost'}`}
onClick={() => setTab(t.id)}
style={{ fontSize: 12, whiteSpace: 'nowrap' }}
>
{t.label}
</button>
))}
</div>

{['users', 'listings', 'bookings'].includes(tab) && (
<div className="form-group" style={{ marginBottom: 12 }}>
<input
className="form-control"
placeholder="🔍 Search..."
value={searchTerm}
onChange={e => setSearchTerm(e.target.value)}
/>
</div>
)}

{tab === 'overview' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>Recent bookings</h3>
{bookings.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No bookings in this period.</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{bookings.slice(0, 15).map(b => (
<div key={b.id} className="card card-body" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
<div style={{ flex: 1, minWidth: 200 }}>
<div style={{ fontWeight: 600, color: 'var(--navy)' }}>{b.listings?.title || 'Unknown item'}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{b.hirer?.full_name || 'Unknown'} → {b.owner?.full_name || 'Unknown'} · {formatDate(b.created_at)}
</div>
</div>
<div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
<div style={{ fontWeight: 700, color: '#16A34A' }}>${b.total_amount?.toFixed(2)}</div>
<span className={`badge badge-${b.status === 'completed' ? 'green' : b.status === 'pending' ? 'amber' : b.status === 'cancelled' ? 'gray' : 'primary'}`}>{b.status}</span>
{b.paid_at && <span className="badge badge-green" style={{ fontSize: 10 }}>✓ Paid</span>}
</div>
</div>
))}
</div>
)}

{ownersWithoutStripe.length > 0 && (
<>
<h3 style={{ fontWeight: 700, marginBottom: 12, marginTop: '2rem', color: 'var(--navy)' }}>
⚠️ Owners without Stripe Connect ({ownersWithoutStripe.length})
</h3>
<div className="card card-body" style={{ fontSize: 13, color: 'var(--text-muted)' }}>
These owners have active listings but haven't connected their bank account:
<ul style={{ marginTop: 8, paddingLeft: 20 }}>
{ownersWithoutStripe.slice(0, 10).map(u => (
<li key={u.id} style={{ marginBottom: 2 }}>
{u.full_name || 'No name'} ({u.suburb || 'No location'})
</li>
))}
</ul>
</div>
</>
)}
</div>
)}

{tab === 'users' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>{filteredUsers.length} users {searchTerm && `(filtered)`}</h3>
{filteredUsers.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No matching users.</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{filteredUsers.map(u => (
<div key={u.id} className="card card-body" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
<div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10 }}>
<div style={{ width: 44, height: 44, borderRadius: '50%', background: u.avatar_url ? 'transparent' : 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
{u.avatar_url ? (
<img src={u.avatar_url} alt={u.full_name || 'User'} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
) : (
<span style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)' }}>
{(u.full_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
</span>
)}
</div>
<div>
<div style={{ fontWeight: 600, color: 'var(--navy)' }}>
{u.full_name || 'No name'}
{u.is_admin && <span className="badge badge-primary" style={{ marginLeft: 6, fontSize: 10 }}>ADMIN</span>}
</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{u.suburb || 'No location'} · Joined {formatDate(u.created_at)}
</div>
</div>
</div>
<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
<span className="badge badge-gray">{((u.review_count || 0) > 0 && u.rating != null && u.rating > 0) ? `⭐ ${u.rating.toFixed(1)} (${u.review_count})` : 'No reviews'}</span>
{u.stripe_charges_enabled
? <span className="badge badge-green">Stripe ✓</span>
: u.stripe_account_id
? <span className="badge badge-amber">Setup incomplete</span>
: <span className="badge badge-gray">No Stripe</span>}
<button className="btn btn-ghost btn-sm" onClick={() => navigate(`/user/${u.id}`)} style={{ fontSize: 11 }}>View profile</button>
</div>
</div>
))}
</div>
)}
</div>
)}

{tab === 'listings' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>{filteredListings.length} listings {searchTerm && `(filtered)`}</h3>
{filteredListings.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No matching listings.</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{filteredListings.map(l => (
<div key={l.id} className="card card-body" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, opacity: l.archived_at ? 0.55 : 1 }}>
<div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10 }}>
{l.images?.[0] ? (
<img src={l.images[0]} alt={l.title} loading="lazy" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
) : (
<ListingPlaceholder listing={l} size={44} radius={8} />
)}
<div>
<div style={{ fontWeight: 600, color: 'var(--navy)' }}>{l.title}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{l.profiles?.full_name} · {l.suburb}, {l.state}{l.price_per_hour ? ` · $${l.price_per_hour}/hr` : l.price_per_day ? ` · $${l.price_per_day}/day` : l.price_per_week ? ` · $${l.price_per_week}/wk` : ''}
</div>
</div>
</div>
<div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
{l.archived_at && <span className="badge badge-gray">Removed{l.removed_by ? ` by ${l.removed_by}` : ''}</span>}
{l.admin_disabled && <span className="badge badge-red" style={{ fontSize: 10 }}>🚫 Disabled</span>}
{l.includes_operator && <span className="badge badge-accent" style={{ fontSize: 10 }}>🛠️ Operator</span>}
<span className={`badge ${l.available ? 'badge-green' : 'badge-amber'}`}>{l.available ? 'Available' : 'Hired'}</span>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(`/listing/${l.id}`)} style={{ fontSize: 11 }}>View</button>
<button className={`btn btn-sm ${l.admin_disabled ? 'btn-primary' : 'btn-ghost'}`} onClick={() => toggleListing(l)} style={{ fontSize: 11 }}>{l.admin_disabled ? 'Enable' : 'Disable'}</button>
{!l.archived_at && <button className="btn btn-danger btn-sm" onClick={() => deleteListing(l.id)} style={{ fontSize: 11 }}>Remove</button>}
</div>
</div>
))}
</div>
)}
</div>
)}

{tab === 'bookings' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>{filteredBookings.length} bookings {searchTerm && `(filtered)`}</h3>
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -6, marginBottom: 12 }}>Everything is listed here, including test bookings and message-only enquiries. The Overview numbers and Growth funnel leave out tests and enquiries.</p>
{filteredBookings.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No matching bookings.</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{filteredBookings.map(b => (
<div key={b.id} className="card card-body" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, opacity: b.is_test ? 0.6 : 1 }}>
<div style={{ flex: 1, minWidth: 200 }}>
<div style={{ fontWeight: 600, color: 'var(--navy)' }}>{b.listings?.title || 'Unknown'}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{b.hirer?.full_name} → {b.owner?.full_name} · {formatDate(b.start_date)} · {b.hours} hrs
</div>
</div>
<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
<div>
<div style={{ fontWeight: 700, color: '#16A34A', fontSize: 14 }}>${b.total_amount?.toFixed(2)}</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Fee: ${b.platform_fee?.toFixed(2)}</div>
</div>
<span className={`badge badge-${b.status === 'completed' ? 'green' : b.status === 'pending' ? 'amber' : b.status === 'cancelled' ? 'gray' : 'primary'}`}>{b.status}</span>
{b.paid_at && <span className="badge badge-green" style={{ fontSize: 10 }}>✓ Paid</span>}
{b.cancelled_at && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>by {b.cancelled_by}</span>}
{b.is_test && <span className="badge badge-gray" style={{ fontSize: 10 }}>🧪 Test</span>}
{b.status === 'enquiry' && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>message only</span>}
<button className="btn btn-ghost btn-sm" onClick={() => toggleTestBooking(b)} style={{ fontSize: 11 }}>{b.is_test ? 'Not a test' : 'Mark as test'}</button>
{b.status !== 'cancelled' && b.status !== 'completed' && (
<button className="btn btn-danger btn-sm" onClick={() => forceCancelBooking(b)} style={{ fontSize: 11 }}>Force cancel</button>
)}
</div>
</div>
))}
</div>
)}
</div>
)}

{tab === 'refunds' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 6, color: 'var(--navy)' }}>💰 Refunds owed</h3>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
Process these refunds manually in Stripe Dashboard. <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Open Stripe →</a>
</p>
{refundsOwed.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: '#16A34A', fontWeight: 600 }}>✅ No refunds to process</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{refundsOwed.map(b => (
<div key={b.id} className="card card-body" style={{ background: '#FEE2E2', border: '1px solid #DC2626' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
<div style={{ fontWeight: 600, color: '#991B1B' }}>💰 ${b.refund_owed?.toFixed(2)} refund to {b.hirer?.full_name}</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Cancelled {formatDate(b.cancelled_at)}</div>
</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
Item: <strong>{b.listings?.title}</strong> · Original total: ${b.total_amount?.toFixed(2)} · Cancelled by: {b.cancelled_by}
</div>
{b.cancellation_reason && (
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>"{b.cancellation_reason}"</div>
)}
{b.stripe_payment_intent_id && (
<div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4, fontFamily: 'monospace' }}>
Payment Intent: {b.stripe_payment_intent_id}
</div>
)}
</div>
))}
</div>
)}
</div>
)}

{tab === 'disputes' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 6, color: 'var(--navy)' }}>⚠️ Disputed returns</h3>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
Owner disagreed with hirer's return condition. Review photos and notes, then decide on deposit release.
</p>
{disputedReturns.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: '#16A34A', fontWeight: 600 }}>✅ No disputes</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
{disputedReturns.map(b => (
<div key={b.id} className="card card-body" style={{ background: '#FFF8E7', border: '1px solid #F5C842' }}>
<div style={{ fontWeight: 700, marginBottom: 6, color: '#8a6d00' }}>⚠️ {b.listings?.title}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
<strong>Hirer:</strong> {b.hirer?.full_name} · <strong>Owner:</strong> {b.owner?.full_name}
</div>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginBottom: 8 }}>
<div style={{ fontSize: 13 }}>
<strong>Hirer says:</strong> {b.return_condition}
{b.return_photo_url && (
<a href={b.return_photo_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 4, color: 'var(--primary)', fontSize: 12 }}>📷 View hirer photo</a>
)}
</div>
<div style={{ fontSize: 13 }}>
<strong>Owner disagrees:</strong>
{b.owner_return_photo_url && (
<a href={b.owner_return_photo_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 4, color: 'var(--primary)', fontSize: 12 }}>📷 View owner photo</a>
)}
{b.owner_return_notes && (
<div style={{ marginTop: 4, fontStyle: 'italic', color: 'var(--text-muted)' }}>"{b.owner_return_notes}"</div>
)}
</div>
</div>
<div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
Deposit: ${b.deposit_amount?.toFixed(2)} · Returned: {formatDate(b.returned_at)}
</div>
<button
className="btn btn-ghost btn-sm"
onClick={() => navigate(`/messages?booking=${b.id}`)}
style={{ marginTop: 8, fontSize: 11 }}
>
💬 Open conversation
</button>
</div>
))}
</div>
)}
</div>
)}

{tab === 'email' && <AdminBulkEmail />}

{tab === 'growth' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>Owner funnel</h3>
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>People who signed up in this period (admins excluded). Click a stage to see who's stuck there. "Started a listing" is only recorded from November 2026.</p>
<div className="card" style={{ padding: 12, marginBottom: 12 }}>
{FUNNEL_STAGES.map((st, i) => {
const n = funnelCounts[i]
const stuck = funnelUsers.filter(u => funnelIndex.get(u.id) === i).length
return (
<div key={st.id} onClick={() => setFunnelStage(funnelStage === i ? null : i)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', background: funnelStage === i ? 'var(--primary-pale)' : 'transparent' }}>
<div style={{ width: 200, fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{st.label}</div>
<div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 6, height: 18, overflow: 'hidden' }}>
<div style={{ width: pct(n, funnelCounts[0]) === '—' ? 0 : pct(n, funnelCounts[0]), height: '100%', background: 'var(--gradient-brand)' }} />
</div>
<div style={{ width: 120, fontSize: 13, textAlign: 'right' }}><strong>{n}</strong> <span style={{ color: 'var(--text-muted)' }}>({pct(n, funnelCounts[0])})</span></div>
<div style={{ width: 90, fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{i < FUNNEL_STAGES.length - 1 ? `${stuck} stuck` : `${stuck} done`}</div>
{st.id === 'published' && (
<button className="btn btn-primary btn-sm" style={{ fontSize: 11, whiteSpace: 'nowrap' }} onClick={e => { e.stopPropagation(); setShowStripeReminder(true) }}>💳 Send Stripe reminder</button>
)}
</div>
)
})}
</div>
{funnelStage !== null && (
<div className="card" style={{ overflowX: 'auto', marginBottom: 24 }}>
<div style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--navy)' }}>
{funnelStage < FUNNEL_STAGES.length - 1
? `${stuckUsers.length} reached "${FUNNEL_STAGES[funnelStage].label}" but not "${FUNNEL_STAGES[funnelStage + 1].label}"`
: `${stuckUsers.length} completed a booking`}
</div>
{stuckUsers.length === 0 ? (
<div style={{ padding: '0 12px 12px', color: 'var(--text-muted)', fontSize: 13 }}>Nobody here.</div>
) : (
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
<thead><tr>{['Name', 'Suburb', 'Signed up', 'Source'].map(h => <th key={h} style={headCell}>{h}</th>)}</tr></thead>
<tbody>
{stuckUsers.map(u => (
<tr key={u.id} onClick={() => navigate(`/user/${u.id}`)} style={{ cursor: 'pointer' }}>
<td style={cell}>{u.full_name || '—'}</td>
<td style={cell}>{u.suburb || '—'}</td>
<td style={cell}>{formatDate(u.created_at)}</td>
<td style={cell}>{u.signup_source || 'Direct / unknown'}</td>
</tr>
))}
</tbody>
</table>
)}
</div>
)}

<h3 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)', marginTop: 12 }}>Signups by source</h3>
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>The campaign or area link they arrived from (last 30 days before signing up). Recorded from November 2026 — older accounts show as Direct / unknown.</p>
{sourceRows.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No sign-ups in this period.</div>
) : (
<div className="card" style={{ overflowX: 'auto' }}>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
<thead><tr>{['Source', 'Signups', 'Published a listing', 'Connected Stripe'].map(h => <th key={h} style={headCell}>{h}</th>)}</tr></thead>
<tbody>
{sourceRows.map(r => (
<tr key={r.source}>
<td style={{ ...cell, fontWeight: 600, color: r.source === 'Direct / unknown' ? 'var(--text-muted)' : 'var(--navy)' }}>{r.source}</td>
<td style={cell}>{r.signups}</td>
<td style={cell}>{r.published} <span style={{ color: 'var(--text-faint)' }}>({pct(r.published, r.signups)})</span></td>
<td style={cell}>{r.stripe} <span style={{ color: 'var(--text-faint)' }}>({pct(r.stripe, r.signups)})</span></td>
</tr>
))}
</tbody>
</table>
</div>
)}
</div>
)}

{showStripeReminder && <StripeReminderModal onClose={() => setShowStripeReminder(false)} />}

{tab === 'wanted' && (() => {
const wantedStatus = (w) => (w.status === 'open' && new Date(w.expires_at) <= new Date() ? 'expired' : w.status)
const removedCount = wantedList.filter(w => w.status === 'removed').length
const shownWanted = showRemovedWanted ? wantedList : wantedList.filter(w => w.status !== 'removed')
const viewW = viewWanted && wantedList.find(w => w.id === viewWanted)
return (
<div>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
<h3 style={{ fontWeight: 700, color: 'var(--navy)', margin: 0 }}>{shownWanted.length} Wanted requests</h3>
<label style={{ fontSize: 13, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
<input type="checkbox" checked={showRemovedWanted} onChange={e => setShowRemovedWanted(e.target.checked)} />
Show removed{removedCount ? ` (${removedCount})` : ''}
</label>
</div>
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Posted in this period. Remove hides a request from the board (nothing is deleted). View opens open requests on the public page. Public page: <a href="/wanted" target="_blank" rel="noreferrer">/wanted</a></p>
{shownWanted.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{wantedList.length ? 'Only removed requests in this period — tick "Show removed" to see them.' : 'No Wanted requests in this period.'}</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{shownWanted.map(w => {
const status = wantedStatus(w)
const reps = wantedReports.filter(r => r.wanted_request_id === w.id)
const cat = CATEGORIES.find(c => c.id === w.category_id)
return (
<div key={w.id} className="card card-body" style={{ opacity: status === 'removed' ? 0.55 : 1 }}>
<div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
<div style={{ minWidth: 200, flex: 1 }}>
<div style={{ fontWeight: 600, color: 'var(--navy)' }}>{cat?.icon} {w.title}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{flagNames[w.user_id] || '—'} · {w.suburb} {w.postcode} · {formatDate(w.created_at)}{w.budget != null ? ` · $${w.budget} ${w.budget_period}` : ''}</div>
{w.note && <div style={{ fontSize: 13, marginTop: 4 }}>{w.note}</div>}
</div>
<div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
<span className={`badge ${status === 'open' ? 'badge-green' : status === 'found' ? 'badge-primary' : 'badge-gray'}`}>{status}</span>
{blockingKinds(detectContactDetails(`${w.title}\n${w.note || ''}`)).length > 0 && <span className="badge badge-red">🔒 Contact details</span>}
{reps.length > 0 && <span className="badge badge-red">⚠️ {reps.length} {reps.length === 1 ? 'report' : 'reports'}</span>}
<button className="btn btn-ghost btn-sm" onClick={() => status === 'open' ? window.open(`/wanted?r=${w.id}`, '_blank', 'noopener') : setViewWanted(w.id)} style={{ fontSize: 11 }}>View</button>
{status !== 'removed' && <button className="btn btn-danger btn-sm" onClick={() => removeWanted(w)} style={{ fontSize: 11 }}>Remove</button>}
</div>
</div>
{reps.length > 0 && (
<div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
{reps.map(r => <div key={r.id}>• {formatDate(r.created_at)}: "{r.reason}"</div>)}
</div>
)}
</div>
)
})}
</div>
)}

{viewW && (() => {
const status = wantedStatus(viewW)
const cat = CATEGORIES.find(c => c.id === viewW.category_id)
const reps = wantedReports.filter(r => r.wanted_request_id === viewW.id)
const row = (label, value) => (
<div style={{ display: 'flex', gap: 12, padding: '6px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
<div style={{ width: 110, flexShrink: 0, color: 'var(--text-muted)' }}>{label}</div>
<div style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value}</div>
</div>
)
return (
<div className="modal-overlay" onClick={() => setViewWanted(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
<h3 style={{ fontWeight: 700, color: 'var(--navy)', margin: 0 }}>{cat?.icon} {viewW.title}</h3>
<span className={`badge ${status === 'found' ? 'badge-primary' : 'badge-gray'}`}>{status}</span>
</div>
<p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>Read-only — this request isn't on the public board ({status === 'found' ? 'the poster marked it found' : status === 'expired' ? 'it expired' : 'an admin removed it'}).</p>
{row('Posted by', flagNames[viewW.user_id] || '—')}
{row('Category', cat?.name || '—')}
{row('Where', `${viewW.suburb} ${viewW.postcode}${viewW.state ? ` ${viewW.state}` : ''}`)}
{row('When', viewW.dates_flexible || !viewW.date_from ? 'Flexible dates' : `${formatDate(viewW.date_from)}${viewW.date_to && viewW.date_to !== viewW.date_from ? ` – ${formatDate(viewW.date_to)}` : ''}`)}
{row('Budget', viewW.budget != null ? `$${viewW.budget} ${viewW.budget_period}` : '—')}
{row('Note', viewW.note || '—')}
{row('Posted', formatDate(viewW.created_at))}
{row(status === 'expired' ? 'Expired' : 'Closed', formatDate(status === 'expired' ? viewW.expires_at : (viewW.closed_at || viewW.expires_at)))}
{reps.length > 0 && row('Reports', reps.map(r => `${formatDate(r.created_at)}: "${r.reason}"`).join('\n'))}
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
{status !== 'removed' && <button className="btn btn-danger btn-sm" onClick={() => { setViewWanted(null); removeWanted(viewW) }}>Remove</button>}
<button className="btn btn-ghost btn-sm" onClick={() => setViewWanted(null)}>Close</button>
</div>
</div>
</div>
)
})()}
</div>
)
})()}

{tab === 'conversations' && <AdminConversations filterKey={timeFilter} cutoff={getDateCutoff(timeFilter)} />}

{tab === 'flags' && (() => {
const perUser = Object.values(contactFlags.reduce((acc, f) => {
acc[f.user_id] = acc[f.user_id] || { user_id: f.user_id, count: 0, last: f.created_at }
acc[f.user_id].count++
if (f.created_at > acc[f.user_id].last) acc[f.user_id].last = f.created_at
return acc
}, {})).sort((a, b) => b.count - a.count)
const shown = flagUser ? contactFlags.filter(f => f.user_id === flagUser) : contactFlags
const where = { message: '💬 Message', wanted: '🔎 Wanted post', listing: '📦 Listing' }
return (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>Contact details flags</h3>
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Each time someone was warned for putting a phone number, email, social media or a link in a message, Wanted post or listing (warning mode — it still went through). Repeat offenders first.</p>
{contactFlags.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No flags in this period.</div>
) : (
<>
<div className="card" style={{ overflowX: 'auto', marginBottom: 16 }}>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
<thead><tr>{['User', 'Flags', 'Latest'].map(h => <th key={h} style={headCell}>{h}</th>)}</tr></thead>
<tbody>
{perUser.map(u => (
<tr key={u.user_id} onClick={() => setFlagUser(flagUser === u.user_id ? null : u.user_id)} style={{ cursor: 'pointer', background: flagUser === u.user_id ? 'var(--primary-pale)' : undefined }}>
<td style={{ ...cell, fontWeight: 600 }}>{flagNames[u.user_id] || '—'}</td>
<td style={{ ...cell, fontWeight: u.count > 2 ? 700 : 400, color: u.count > 2 ? 'var(--red)' : undefined }}>{u.count}</td>
<td style={cell}>{formatDate(u.last)}</td>
</tr>
))}
</tbody>
</table>
</div>
<div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>
{flagUser ? `${flagNames[flagUser] || 'This user'}'s flags` : 'All flags, newest first'} {flagUser && <button onClick={() => setFlagUser(null)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12 }}>show all</button>}
</div>
<div className="card" style={{ overflowX: 'auto' }}>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
<thead><tr>{['When', 'User', 'Where', 'What', 'Text'].map(h => <th key={h} style={headCell}>{h}</th>)}</tr></thead>
<tbody>
{shown.map(f => (
<tr key={f.id}>
<td style={{ ...cell, whiteSpace: 'nowrap' }}>{formatDate(f.created_at)}</td>
<td style={cell}>{flagNames[f.user_id] || '—'}</td>
<td style={{ ...cell, whiteSpace: 'nowrap' }}>{where[f.location] || f.location}</td>
<td style={cell}>{(f.kinds || []).map(k => CONTACT_KIND_LABELS[k] || k).join(', ')}</td>
<td style={{ ...cell, color: 'var(--text-muted)', maxWidth: 360 }}>{f.excerpt}</td>
</tr>
))}
</tbody>
</table>
</div>
</>
)}
</div>
)
})()}

{tab === 'searches' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>Searches with no results</h3>
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{searchMisses.length} searches found nothing in this period ({missGroups.length} different). No personal details are recorded.</p>
{missGroups.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No empty searches in this period.</div>
) : (
<div className="card" style={{ overflowX: 'auto' }}>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
<thead><tr>{['Search term', 'Location', 'Category', 'Times', 'Last searched'].map(h => <th key={h} style={headCell}>{h}</th>)}</tr></thead>
<tbody>
{missGroups.map(g => (
<tr key={g.key}>
<td style={{ ...cell, fontWeight: 600, color: g.term ? 'var(--navy)' : 'var(--text-faint)' }}>{g.term || '—'}</td>
<td style={cell}>{g.location || '—'}</td>
<td style={cell}>{g.category || '—'}</td>
<td style={{ ...cell, fontWeight: g.count > 1 ? 700 : 400 }}>{g.count}</td>
<td style={cell}>{formatDate(g.last)}</td>
</tr>
))}
</tbody>
</table>
</div>
)}
</div>
)}

{tab === 'removed' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>{deletedAccounts.length} deleted {deletedAccounts.length === 1 ? 'account' : 'accounts'}</h3>
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>No personal details are kept — only the state, the month they joined and how many listings they had.</p>
{deletedAccounts.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: 24 }}>No deleted accounts in this period.</div>
) : (
<div className="card" style={{ overflowX: 'auto', marginBottom: 24 }}>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
<thead>
<tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
{['Deleted', 'State', 'Member since', 'Listings', 'Reason'].map(h => <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
</tr>
</thead>
<tbody>
{deletedAccounts.map(d => (
<tr key={d.id}>
<td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{formatDate(d.deleted_at)}</td>
<td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{d.state || '—'}</td>
<td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{d.member_since ? new Date(d.member_since).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : '—'}</td>
<td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{d.number_of_listings}</td>
<td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', color: d.reason ? 'var(--text)' : 'var(--text-faint)' }}>{d.reason || 'Not given'}</td>
</tr>
))}
</tbody>
</table>
</div>
)}

<h3 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>{removedListings.length} removed {removedListings.length === 1 ? 'listing' : 'listings'}</h3>
{removedListings.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No removed listings in this period.</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{removedListings.map(l => (
<div key={l.id} className="card card-body" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
<div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10 }}>
{l.images?.[0] ? (
<img src={l.images[0]} alt={l.title} loading="lazy" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
) : (
<ListingPlaceholder listing={l} size={44} radius={8} />
)}
<div>
<div style={{ fontWeight: 600, color: 'var(--navy)' }}>{l.title}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{l.profiles?.full_name || 'Unknown owner'} · {[l.suburb, l.state].filter(Boolean).join(', ')}</div>
</div>
</div>
<div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
<span style={{ color: 'var(--text-muted)' }}>{formatDate(l.archived_at)}</span>
<span className={`badge ${l.removed_by === 'admin' ? 'badge-red' : 'badge-gray'}`}>By {l.removed_by || 'owner'}</span>
<span style={{ color: l.removal_reason ? 'var(--text)' : 'var(--text-faint)' }}>{l.removal_reason || 'No reason given'}</span>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(`/listing/${l.id}`)} style={{ fontSize: 11 }}>View</button>
</div>
</div>
))}
</div>
)}
</div>
)}

{tab === 'reports' && (
<div>
<h3 style={{ fontWeight: 700, marginBottom: 6, color: 'var(--navy)' }}>🚩 User reports</h3>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
Latest 50 reports filed. Review for abuse patterns.
</p>
{reports.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: '#16A34A', fontWeight: 600 }}>✅ No reports</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{reports.map(r => (
<div key={r.id} className="card card-body" style={{ background: '#FEE2E2', border: '1px solid #FCA5A5' }}>
<div style={{ fontSize: 13, color: '#991B1B', marginBottom: 6 }}>
<strong>{r.reporter?.full_name || 'Unknown'}</strong> reported <strong>{r.reported?.full_name || 'Unknown'}</strong>
</div>
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{formatDate(r.created_at)}</div>
<div style={{ fontSize: 13, fontStyle: 'italic', background: 'white', padding: '8px 10px', borderRadius: 6 }}>
"{r.reason}"
</div>
{r.booking_id && (
<button
className="btn btn-ghost btn-sm"
onClick={() => navigate(`/messages?booking=${r.booking_id}`)}
style={{ marginTop: 8, fontSize: 11, alignSelf: 'flex-start' }}
>
💬 Open conversation
</button>
)}
</div>
))}
</div>
)}
</div>
)}
</div>
</div>
)
}
